import { useState, useRef, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Icon } from '@/lib/icons';
import { COUNTRIES, type CountryData, formatPhoneNational, detectCountryFromPhone } from '@/lib/countries';
import { AUTH_INPUT_CLASS } from '@/lib/signup.constants';

interface CountryPhoneInputProps {
  country: string;
  phone: string;
  onCountryChange: (country: CountryData) => void;
  onPhoneChange: (phone: string) => void;
  countryPlaceholder: string;
  phonePlaceholder: string;
  /** id of the phone field, so a visible <Label htmlFor> can name it. */
  phoneInputId?: string;
}

export const CountryPhoneInput = ({
  country,
  phone,
  onCountryChange,
  onPhoneChange,
  countryPlaceholder,
  phonePlaceholder,
  phoneInputId,
}: CountryPhoneInputProps) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedCountry = COUNTRIES.find((c) => c.name === country) || null;

  // Compute an inner placeholder that excludes the dial prefix and matches the country's phone length
  const innerPlaceholder = (() => {
    if (selectedCountry) {
      // create a fake raw number with the right number of digits, format it and replace digits with 'x'
      const raw = '1'.repeat(selectedCountry.digits || 0);
      const formatted = formatPhoneNational(selectedCountry.dial, raw, selectedCountry.digits);
      return formatted.replace(/^\+\d+\s*/, '').replace(/\d/g, 'x');
    }
    if (phonePlaceholder) {
      // remove leading +digits and mask any digits so placeholder doesn't show real numbers
      return phonePlaceholder.replace(/^\+\d+\s*/, '').replace(/\d/g, 'x');
    }
    return '';
  })();

  const handlePhoneInput = useCallback(
    (raw: string) => {
      const stripped = raw.replace(/[^\d+\s]/g, '');
      const detected = detectCountryFromPhone(stripped);
      const countryForFormatting = detected || selectedCountry;

      if (detected && detected.iso !== selectedCountry?.iso) {
        onCountryChange(detected);
      }

      if (countryForFormatting) {
        onPhoneChange(formatPhoneNational(countryForFormatting.dial, stripped, countryForFormatting.digits));
        return;
      }

      onPhoneChange(stripped);
    },
    [selectedCountry, onPhoneChange, onCountryChange],
  );

  const handleSelect = useCallback(
    (c: CountryData) => {
      onCountryChange(c);
      const national = phone.replace(/^\+\d+\s*/, '').replace(/\D/g, '');
      const formatted = formatPhoneNational(c.dial, national, c.digits);
      onPhoneChange(formatted);
      setOpen(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [phone, onCountryChange, onPhoneChange],
  );

  return (
    <div className="space-y-2">
      {/* Combined country picker + phone input as single control */}
      <div className={`flex items-center border ${AUTH_INPUT_CLASS}`}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={selectedCountry ? `${countryPlaceholder} : ${selectedCountry.name}` : countryPlaceholder}
              className="flex h-full shrink-0 items-center gap-1.5 rounded-l-xl px-3 text-sm text-muted-foreground hover:text-foreground focus-ring"
            >
              <Icon name="search" className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="px-1 font-medium text-foreground select-none">{selectedCountry ? selectedCountry.dial : ''}</span>
              <Icon name="chevronDown" className="w-4 h-4 text-faint-foreground" />
            </button>
          </PopoverTrigger>

          <PopoverContent className="w-[min(320px,calc(100vw-2rem))] p-0" align="start">
            < Command>
              <CommandInput placeholder={countryPlaceholder} />
              <CommandList>
                <CommandEmpty>{countryPlaceholder}</CommandEmpty>
                <CommandGroup className="max-h-[280px] overflow-y-auto">
                  {COUNTRIES.map((c) => (
                    <CommandItem
                      key={c.iso}
                      value={`${c.name} ${c.dial}`}
                      onSelect={() => handleSelect(c)}
                      className="cursor-pointer"
                    >
                      <span className="mr-2 text-muted-foreground text-xs w-10 shrink-0">{c.dial}</span>
                      <span className="truncate">{c.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <input
          ref={inputRef}
          id={phoneInputId}
          type="tel"
          inputMode="numeric"
          placeholder={innerPlaceholder}
          value={phone.replace(/^\+\d+\s*/, '')}
          onChange={(e) => handlePhoneInput(e.target.value)}
          className="min-w-0 flex-1 bg-transparent border-0 outline-none px-3 text-sm text-foreground placeholder:text-faint-foreground"
        />
      </div>
    </div>
  );
};
